import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DailyLogService } from '@providers/api/daily-log/daily-log';
import {
  DailyActivityCategoryParams,
  DailyActivityParams,
  GetDailyActivitiesResponse,
  GetDailyActivityCategoriesResponse,
  GetUserDailyActivitiesResponse,
  PostUserDailyActivityResponse,
  UserDailyActivityParams,
  UserDailyActivityPayload
} from './activity.interface';

/**
 * Daily activity API.
 */
@Injectable()
export class DailyActivityAPI extends DailyLogService {
  /**
   * Creates a new user daily activity with profile ID from auth context.
   *
   * @param {UserDailyActivityPayload} payload Payload.
   * @returns {Observable<PostUserDailyActivityResponse>} Observable of the response.
   */
  createUserDailyActivity(
    payload: UserDailyActivityPayload
  ): Observable<PostUserDailyActivityResponse> {
    return this.post<PostUserDailyActivityResponse>(
      'user-daily-activities',
      payload
    );
  }

  /**
   * Deletes a user daily activity by ID with validation of owner via profileId.
   *
   * @param {string} userDailyActivityId User daily activity id.
   * @returns {Observable<PostUserDailyActivityResponse>} Observable of the response.
   */
  deleteUserDailyActivity(
    userDailyActivityId: string
  ): Observable<PostUserDailyActivityResponse> {
    return this.delete<PostUserDailyActivityResponse>(
      `user-daily-activities/${userDailyActivityId}`
    );
  }

  /**
   * Retrieves all daily activities with optional filtering, pagination, and detail inclusion.
   *
   * @param {DailyActivityParams} params Parameters.
   * @returns {Observable<GetDailyActivitiesResponse>} Observable of the response.
   */
  getDailyActivities(
    params: DailyActivityParams
  ): Observable<GetDailyActivitiesResponse> {
    return this.get<GetDailyActivitiesResponse>('daily-activities', params);
  }

  /**
   * Retrieves all daily activity categories with optional filtering and pagination.
   *
   * @param {DailyActivityCategoryParams} params Params.
   * @returns {Observable<GetDailyActivityCategoriesResponse>} Observable of the response.
   */
  getDailyActivityCategories(
    params: DailyActivityCategoryParams
  ): Observable<GetDailyActivityCategoriesResponse> {
    return this.get<GetDailyActivityCategoriesResponse>(
      'daily-activity-categories',
      params
    );
  }

  /**
   * Retrieves user daily activities filtered by date range and profile ID from auth context.
   *
   * @param {UserDailyActivityParams} params Params.
   * @returns {Observable<GetUserDailyActivitiesResponse>} Observable of the response.
   */
  getUserDailyActivities(
    params: UserDailyActivityParams
  ): Observable<GetUserDailyActivitiesResponse> {
    return this.get<GetUserDailyActivitiesResponse>(
      'user-daily-activities',
      params
    );
  }

  /**
   * Updates an existing user daily activity with validation of owner via profileId.
   *
   * @param {string} userDailyActivityId User daily activity id.
   * @param {UserDailyActivityPayload} payload Payload.
   * @returns {Observable<PostUserDailyActivityResponse>} Observable of the response.
   */
  updateUserDailyActivity(
    userDailyActivityId: string,
    payload: UserDailyActivityPayload
  ): Observable<PostUserDailyActivityResponse> {
    return this.put<PostUserDailyActivityResponse>(
      `user-daily-activities/${userDailyActivityId}`,
      payload
    );
  }
}
